import { NgFor, NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	Optional,
	Output
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IconComponent, TypographyComponent } from 'components';
import {
	PreviewSlide,
	ReportDateRange,
	SlideType,
	SummaryTextWidget,
	TitleWidget
} from '../../../../models/preview/preview.models';
import {
	ReportBlockData,
	ReportBlockRender
} from '../../../../services/report-blocks.service';
import { blockSubjectNames } from '../../../../services/report-block-chart-config';
import {
	ReportSectionChartSwitch,
	sectionChartSwitch
} from '../../../../services/report-chart-types';
import {
	NO_SLIDE_TEXT_FIELDS,
	ReportSlideTextFields,
	slideTextFields
} from '../../../../services/report-inline-edit.service';
import { ReportLanguageService } from '../../../../services/report-language.service';
import { reportMessage } from '../../../../services/report-messages';
import {
	applyContentItemOverrides,
	REPORT_DEFAULT_ACCENT_COLOR
} from '../../../../services/report-preview-utils';
import { ReportContentItem } from '../../../../services/report-settings.service';
import {
	numberSource,
	ReportSource,
	reportSourceDetail
} from '../../../../services/report-source';
import {
	comparisonChartSwitch,
	entityMatrix,
	sourceComparisonBlock
} from '../../../../services/report-source-sections';
import { ReportChartSwitchComponent } from '../../components/report-chart-switch/report-chart-switch.component';
import { ReportInlineTextComponent } from '../../components/report-inline-text/report-inline-text.component';
import { ReportSlideBrandComponent } from '../../components/report-slide-brand/report-slide-brand.component';
import { ReportChartDrawingComponent } from '../../widgets/chart-drawing/chart-drawing.component';

interface MatrixRow {
	entity: string;
	color: string;
	cells: Array<{ value: string; source: string }>;
}

/**
 * A fixed section of a report about several entities — a competitor
 * project's competitors — drawn as ONE slide comparing them.
 *
 * Drawn by the slide host in place of the section's own slide whenever the
 * section's answer compares (report-source-sections.ts): the section's own
 * slide draws one entity, its callouts and its tables are one entity's, and
 * there is no honest way to fold a second into them. So this keeps the
 * section's frame — its title and summary, typed into in the editor as on
 * the section's own slide, under the same widget ids, its date and its footer
 * — and draws the merged block in the chart area, one series per entity,
 * switchable to any type the block can take. The Executive Summary is its
 * headline figures entity by metric — an entity a row, a figure a column —
 * each cell with its own source.
 *
 * What the answer says of its numbers is printed under them: the entity a
 * comparison dropped for having too few mentions, by name.
 */
@Component({
	standalone: true,
	selector: 'app-source-comparison-slide',
	templateUrl: './source-comparison-slide.component.html',
	styleUrls: ['./source-comparison-slide.component.scss'],
	imports: [
		NgIf,
		NgFor,
		// TRAP: matTooltip is silently inert without this.
		MatTooltipModule,
		IconComponent,
		TypographyComponent,
		ReportInlineTextComponent,
		ReportChartSwitchComponent,
		ReportChartDrawingComponent,
		ReportSlideBrandComponent
	]
})
export class SourceComparisonSlideComponent implements OnChanges {
	@Input() item!: ReportContentItem;
	@Input() brandId!: string;
	@Input() dateRange!: ReportDateRange;
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;
	@Input() blocks: ReportBlockData[] = [];
	@Input() source: ReportSource | null = null;
	@Output() slideBuilt = new EventEmitter<PreviewSlide>();
	@Output() chartTypeChange = new EventEmitter<ReportBlockRender | null>();

	textFields: ReportSlideTextFields = NO_SLIDE_TEXT_FIELDS;

	title = '';
	titleColor = REPORT_DEFAULT_ACCENT_COLOR;
	dateLabel = '';
	summaryText = '';
	tag = '';
	compared = '';
	notes: string[] = [];
	switchable = false;
	measures: string[] = [];
	matrixRows: MatrixRow[] = [];
	// CONTRACT: read by the slide host by this name as the slide is built
	// (`withChartNumbers`), for the figures a text quotes.
	chartSwitch: ReportSectionChartSwitch = sectionChartSwitch('', null, null);

	constructor(
		@Optional() private reportLanguage: ReportLanguageService | null
	) {}

	ngOnChanges(): void {
		if (!this.item || !this.dateRange) return;

		const slide = applyContentItemOverrides(
			this.buildSlide(),
			this.item,
			this.themeColor
		);
		this.updateViewModel(slide);
		this.slideBuilt.emit(slide);
	}

	// CONTRACT: the widget ids the section's own slide gives its heading, date
	// and summary — saved edits live on them, so both slides show the same words.
	private buildSlide(): PreviewSlide {
		const prefix = `${this.item.id}_${this.brandId}`;
		const title = this.item.title || this.item.name;
		return {
			id: `${prefix}_slide`,
			slideType: this.item.id as SlideType,
			title,
			description: this.item.description || '',
			dateRange: this.dateRange,
			widgets: [
				{
					id: `${prefix}_title`,
					kind: 'title',
					visible: true,
					content: { text: title, color: REPORT_DEFAULT_ACCENT_COLOR }
				} as TitleWidget,
				{
					id: `${prefix}_date`,
					kind: 'date_range',
					visible: true,
					content: { text: this.dateRange.label }
				} as TitleWidget,
				{
					id: `${prefix}_summary`,
					kind: 'summary_text',
					visible: true,
					content: { text: this.item.description || '' }
				} as SummaryTextWidget
			]
		};
	}

	private updateViewModel(slide: PreviewSlide): void {
		this.textFields = slideTextFields(slide);
		const hidden = new Set(slide.overrides?.hiddenWidgetIds ?? []);
		const shown = (kind: string) =>
			slide.widgets.find(
				widget => widget.kind === kind && !hidden.has(widget.id)
			);
		const titleWidget = shown('title') as TitleWidget | undefined;
		const dateWidget = shown('date_range') as TitleWidget | undefined;
		const summaryWidget = shown('summary_text') as
			| SummaryTextWidget
			| undefined;

		this.title = titleWidget?.content.text ?? slide.title;
		this.titleColor =
			titleWidget?.content.color ?? REPORT_DEFAULT_ACCENT_COLOR;
		this.dateLabel =
			dateWidget?.content.text ?? slide.dateRange?.label ?? '';
		this.summaryText = summaryWidget?.content.text ?? '';
		this.tag = (this.item.name || this.item.id).toLowerCase();

		const block = sourceComparisonBlock(this.item.id, this.blocks);
		const names = block ? blockSubjectNames(block) : [];
		this.compared =
			names.length > 1
				? names.join(
						reportMessage(
							this.reportLanguage?.language,
							'source.names.versus'
						)
				  )
				: '';
		this.notes = Array.from(
			new Set(
				this.blocks
					.filter(one => !one.empty && one.note)
					.map(one => one.note as string)
			)
		);
		this.chartSwitch = comparisonChartSwitch(
			this.item.id,
			this.item.chartType,
			block
		);
		this.switchable = this.chartSwitch.types.length > 0;

		const matrix = this.switchable
			? null
			: entityMatrix(this.chartSwitch.block);
		this.measures = matrix?.measures ?? [];
		this.matrixRows = (matrix?.rows ?? []).map(row => ({
			entity: row.entity,
			color: row.color,
			cells: row.values.map((value, at) => ({
				value,
				source: this.source
					? reportSourceDetail(
							numberSource(
								this.source,
								matrix!.measures[at],
								row.entity
							)
					  )
					: ''
			}))
		}));
	}
}
